import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom-v5-compat'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import { useSnackbar } from 'notistack'

import DialogConfirm from '~/components/DialogConfirm'
import Section from '~/components/Section'
import { type CreateFormProps, type EditFormProps, useGiftWithPurchaseForm } from '../hooks'
import type { GiftWithPurchaseCampaignDetailInput } from '../schemas'
import CampaignInfo from './CampaignInfo'
import CampaignProducts from './CampaignProducts'
import Tiers from './Tiers'

const FOOTER_HEIGHT_SPACING = 8

function GiftWithPurchaseFormFooter({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                position: 'fixed',
                bottom: 0,
                right: 0,
                width: 'calc(100vw - var(--app-navbar-width))',
                maxHeight: (theme) => theme.spacing(FOOTER_HEIGHT_SPACING),
                px: 1,
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid',
                borderTopColor: 'divider',
            }}
        >
            {children}
        </Box>
    )
}

type BaseProps = {
    isSubmitting: boolean
    onSubmit: (body: GiftWithPurchaseCampaignDetailInput) => void
}

type Props = BaseProps & (CreateFormProps | EditFormProps)

export default function GiftWithPurchaseForm({ isSubmitting, onSubmit, ...props }: Props) {
    const { t } = useTranslation(['common', 'gift_with_purchase'])
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const [open, setOpen] = useState(false)

    const {
        control,
        formState: { isDirty },
        handleSubmit,
    } = useGiftWithPurchaseForm(props)

    return (
        <Stack
            component="form"
            gap={2}
            sx={{ pb: FOOTER_HEIGHT_SPACING }}
            onSubmit={handleSubmit(onSubmit, (errors) => {
                console.error(errors)
                enqueueSnackbar(t('gift_with_purchase:form.validation.failed'), {
                    variant: 'error',
                })
            })}
        >
            <CampaignInfo control={control} />
            <Tiers control={control} />
            <CampaignProducts control={control} />

            <GiftWithPurchaseFormFooter>
                <div>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            if (isDirty) {
                                setOpen(true)
                            } else {
                                navigate(-1)
                            }
                        }}
                    >
                        {t('common:back')}
                    </Button>
                </div>
                <div>
                    <LoadingButton
                        variant="contained"
                        type="submit"
                        disabled={!isDirty}
                        loading={isSubmitting}
                    >
                        {t('common:save')}
                    </LoadingButton>
                </div>
            </GiftWithPurchaseFormFooter>

            <DialogConfirm
                open={open}
                title={t('gift_with_purchase:form.dialog.title')}
                message={t('gift_with_purchase:form.dialog.confirm_leave')}
                confirmText={t('common:leave')}
                onClose={() => setOpen(false)}
                onConfirm={() => navigate(-1)}
            />
        </Stack>
    )
}

function GiftWithPurchaseFormSkeleton({ maxWait = 200 }: { maxWait?: number }) {
    const { t } = useTranslation('gift_with_purchase')

    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setIsLoading(true), maxWait)
        return () => clearTimeout(timeout)
    }, [maxWait])

    if (!isLoading) {
        return null
    }

    return (
        <Stack gap={2}>
            <Section
                component={Stack}
                gap={1}
                header={<Section.Header label={t('form.campaign_info.title')} />}
            >
                <Stack gap={1}>
                    <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" gap={1}>
                            <Skeleton variant="rounded" width={200} height={36} />
                            <Skeleton variant="rounded" width={200} height={36} />
                            <Skeleton variant="rounded" width={200} height={36} />
                        </Stack>

                        <Stack direction="row" gap={1}>
                            <Skeleton variant="text" width={100} height={36} />
                            <FormControlLabel
                                disabled
                                control={<Switch />}
                                label={t('form.campaign_info.field_active')}
                                labelPlacement="start"
                            />
                        </Stack>
                    </Stack>

                    <Stack direction="row" gap={2}>
                        <FormControlLabel
                            disabled
                            control={<Checkbox />}
                            label={t('form.campaign_info.field_can_be_combined')}
                            labelPlacement="start"
                        />
                    </Stack>
                </Stack>
            </Section>

            <Section
                component={Stack}
                gap={1}
                header={<Section.Header label={t('form.tiers.title')} />}
            >
                <Stack direction="row" gap={2}>
                    <Skeleton variant="rounded" width={400} height={300} />
                    <Skeleton variant="rounded" width={400} height={300} />
                </Stack>
            </Section>

            <Section
                component={Stack}
                gap={1}
                header={<Section.Header label={t('form.campaign_products.title')} />}
            >
                <FormControlLabel
                    disabled
                    control={<Checkbox />}
                    label={t('form.campaign_info.field_applies_to_all_products')}
                    labelPlacement="start"
                />
                <Skeleton variant="rounded" width="100%" height={150} />
            </Section>
        </Stack>
    )
}

GiftWithPurchaseForm.Skeleton = GiftWithPurchaseFormSkeleton
